import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconChevronDown, IconArrowRight } from '@tabler/icons-react';
import { NAV_SECTIONS, NAV_PRICING, type NavSection } from './nav-data';

const OPEN_DELAY_MS = 70;
const CLOSE_DELAY_MS = 180;

/** Desktop mega menu (lg and up). Solid panels, no blur, warm tokens only so dark mode follows. */
export function MegaMenu() {
  const [openId, setOpenId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const pinned = useRef(false);
  const { pathname } = useLocation();

  const clear = () => { if (timer.current) clearTimeout(timer.current); };
  const schedule = useCallback((id: string | null, delay: number) => {
    clear();
    timer.current = setTimeout(() => setOpenId(id), delay);
  }, []);
  const closeNow = useCallback(() => { clear(); pinned.current = false; setOpenId(null); }, []);

  useEffect(() => { closeNow(); }, [pathname, closeNow]);
  useEffect(() => () => clear(), []);

  useEffect(() => {
    if (!openId) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeNow();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const id = openId;
      closeNow();
      rootRef.current?.querySelector<HTMLElement>(`[data-trigger="${id}"]`)?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openId, closeNow]);

  const active = NAV_SECTIONS.find((s) => s.id === openId);

  return (
    <div
      ref={rootRef}
      className="hidden lg:block relative"
      onMouseLeave={() => { if (!pinned.current) schedule(null, CLOSE_DELAY_MS); }}
      onMouseEnter={clear}
    >
      <nav aria-label="Main" className="flex items-center gap-1">
        {NAV_SECTIONS.map((s) => {
          const on = openId === s.id;
          return (
            <button
              key={s.id}
              type="button"
              data-trigger={s.id}
              aria-expanded={on}
              aria-controls={`mega-${s.id}`}
              onMouseEnter={() => { pinned.current = false; schedule(s.id, openId ? 0 : OPEN_DELAY_MS); }}
              onClick={() => {
                // A click on a menu that hover already opened pins it; a second click closes it.
                if (on && pinned.current) { closeNow(); return; }
                pinned.current = true;
                clear();
                setOpenId(s.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setOpenId(s.id);
                  requestAnimationFrame(() => rootRef.current?.querySelector<HTMLElement>(`#mega-${s.id} a`)?.focus());
                }
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition-colors"
              style={{ color: on ? 'var(--warm-text)' : 'var(--warm-muted)' }}
            >
              {s.label}
              <IconChevronDown size={14} stroke={1.75} className="transition-transform duration-200" style={{ transform: on ? 'rotate(180deg)' : 'none' }} />
            </button>
          );
        })}
        <Link
          to={NAV_PRICING.to}
          onMouseEnter={() => schedule(null, 0)}
          className="px-3 py-1.5 text-sm rounded-md transition-colors"
          style={{ color: pathname === NAV_PRICING.to ? 'var(--warm-text)' : 'var(--warm-muted)' }}
        >
          {NAV_PRICING.label}
        </Link>
      </nav>

      {active && <Panel key={active.id} section={active} onNavigate={closeNow} />}
    </div>
  );
}

function Panel({ section, onNavigate }: { section: NavSection; onNavigate: () => void }) {
  const cols = section.groups.length;
  return (
    <div
      id={`mega-${section.id}`}
      className="mega-panel absolute top-full pt-3 left-1/2 z-50"
      style={{ width: cols === 1 ? 'min(760px, calc(100vw - 2.5rem))' : 'min(1120px, calc(100vw - 2.5rem))', transform: 'translateX(-50%)' }}
    >
      <div
        className="rounded-2xl p-6 grid gap-8 shadow-[0_18px_48px_-12px_rgba(0,0,0,0.18)]"
        style={{
          background: 'var(--warm-card)',
          border: '1px solid var(--warm-border)',
          gridTemplateColumns: section.feature ? `repeat(${cols}, minmax(0, 1fr)) 260px` : `repeat(${cols}, minmax(0, 1fr))`,
        }}
      >
        {section.groups.map((g) => (
          <div key={g.title}>
            <p className="text-[11px] font-semibold uppercase tracking-wider pb-2.5 mb-2" style={{ color: 'var(--warm-muted)', borderBottom: '1px solid var(--warm-border)' }}>
              {g.title}
            </p>
            <ul className={cols === 1 ? 'grid grid-cols-2 gap-x-3 gap-y-0.5' : 'space-y-0.5'}>
              {g.items.map((it) => (
                <li key={it.to + it.label}>
                  <Link
                    to={it.to}
                    onClick={onNavigate}
                    className="mega-item group flex items-start gap-3 rounded-xl px-2.5 py-2"
                  >
                    <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'var(--warm-inset)', color: 'var(--warm-text)' }}>
                      <it.icon size={17} stroke={1.5} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium leading-5" style={{ color: 'var(--warm-text)' }}>{it.label}</span>
                      <span className="block text-xs leading-4 mt-0.5" style={{ color: 'var(--warm-muted)' }}>{it.desc}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {section.feature && (
          <Link
            to={section.feature.to}
            onClick={onNavigate}
            className="mega-feature flex flex-col rounded-xl p-5 self-stretch"
            style={{ background: 'var(--warm-inset)', border: '1px solid var(--warm-border)' }}
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--warm-muted)' }}>{section.feature.eyebrow}</span>
            <span className="font-display text-2xl leading-tight mt-2" style={{ color: 'var(--warm-text)' }}>{section.feature.title}</span>
            <span className="text-sm mt-2 leading-snug" style={{ color: 'var(--warm-muted)' }}>{section.feature.body}</span>
            <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--warm-text)' }}>
              {section.feature.cta} <IconArrowRight size={15} stroke={1.75} />
            </span>
          </Link>
        )}
      </div>
    </div>
  );
}
